import { Flex, Text, Button, Loader, Icon, Textarea } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import logError from 'Helpers/logError';
import { dateCalendar } from 'App/helpers/DateHelpers';
import RatingScale, { Rating } from './RatingScale';

import './OrderForecast.scss';

type Signal = { signal: string; source: string; severity?: string };
type Action = { action: string; priority: string; reasoning: string };

type JobState = 'queued' | 'running' | 'completed' | 'failed';

type ForecastPayload = {
	status: string;
	confidence: string;
	rating?: Rating;
	ratingHeadline?: string;
	summary: string | string[];
	depth: string;
	analyzedAt: string;
	closingDateAssessment: { likelihood: string; estimatedSlippageDays: number; reasoning: string };
	positiveSignals: Signal[];
	riskSignals: Signal[];
	communicationInsights: {
		pricingConcerns: boolean;
		timelineAlignment: string;
		customerEngagement: string;
		keyTopics: string[];
		overallTone: string;
	};
	stakeholderAssessment: { adequate: boolean; missingRoles: string[]; suggestedActions: string[] };
	icpMatch?: { match: string; reasoning: string };
	recommendedActions: Action[];
};

type ForecastData = {
	forecastId: number | null;
	jobState: JobState;
	forecast: ForecastPayload | null;
};

type EnqueueResponse = { forecastId: number; jobState: JobState };

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const STATUS_TO_RATING: Record<string, Rating> = {
	on_track: 'on_track',
	needs_attention: 'slightly_at_risk',
	at_risk: 'at_risk',
	stalled: 'unlikely'
};

const deriveRating = (f: ForecastPayload): Rating => f.rating ?? STATUS_TO_RATING[f.status] ?? 'slightly_at_risk';

const deriveHeadline = (f: ForecastPayload): string => {
	if (f.ratingHeadline) return f.ratingHeadline;
	const s = Array.isArray(f.summary) ? f.summary[0] : f.summary;
	return s || '';
};

const classes = new BemClass('OrderForecast');

const translateEnum = (t: (key: string) => string, group: string, value: string | undefined | null): string => {
	if (!value) return '';
	const key = `ai.dealForecast.${group}.${value}`;
	const translated = t(key);
	return translated === key ? value.replace(/_/g, ' ') : translated;
};

// Re-analyze button — relies on `loading={refreshing}` to disable during
// the in-flight window, so chained misclicks can't pile up. Server-side
// `peekInFlightForecast` short-circuits any duplicate that does arrive.
const ReAnalyzeButton = ({ onConfirm, refreshing }: { onConfirm: () => void; refreshing: boolean }) => {
	const { t } = useTranslation();
	return (
		<Button
			size="sm"
			type="link"
			className={classes.elem('reanalyze').b()}
			onClick={e => {
				e.stopPropagation();
				if (!refreshing) onConfirm();
			}}
			loading={refreshing}
		>
			<Icon name="refresh" /> {t('ai.dealForecast.reAnalyze')}
		</Button>
	);
};

const FeedbackBar = ({ forecastId, orderId }: { forecastId: number | null; orderId: number }) => {
	const { t } = useTranslation();
	const [submitted, setSubmitted] = useState(false);
	const [rating, setRating] = useState<1 | 2 | null>(null);
	const [comment, setComment] = useState('');

	if (!forecastId) return null;

	// Append-only — every call inserts a new feedback row server-side. Rapid
	// rating flips and a final Send each become their own row; that's a more
	// honest representation of user intent than coalescing.
	const postFeedback = async (r: 1 | 2, c?: string) => {
		try {
			await fetch(`/api/v2/ai/deal-forecast/${forecastId}/feedback`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ rating: r, comment: c || undefined, orderId })
			});
		} catch (err) {
			logError(err, 'OrderForecast.feedback');
		}
	};

	const onRate = (next: 1 | 2) => {
		// Toggling the active button deselects in the UI but doesn't retract
		// the saved feedback — the row stays, next rate click will update it.
		if (rating === next) {
			setRating(null);
			return;
		}
		setRating(next);
		postFeedback(next, comment);
	};

	const submit = async () => {
		if (rating === null) return;
		await postFeedback(rating, comment);
		setSubmitted(true);
	};

	if (submitted) {
		return (
			<div className={classes.elem('feedback-bar').mod({ sent: true }).b()}>
				<Icon name="check" />
				<Text size="sm">{t('ai.dealForecast.feedback.thanks')}</Text>
			</div>
		);
	}

	return (
		<div className={classes.elem('feedback-bar').b()}>
			<div className={classes.elem('feedback-top').b()}>
				<Text size="sm" color="grey-10" className={classes.elem('feedback-q').b()}>
					{t('ai.dealForecast.feedback.useful')}
				</Text>
				<div className={classes.elem('feedback-group').b()} role="group">
					<button
						type="button"
						className={classes
							.elem('feedback-btn')
							.mod({ active: rating === 2 })
							.b()}
						aria-pressed={rating === 2}
						onClick={() => onRate(2)}
					>
						<Icon name="thumbs-up" /> {t('ai.dealForecast.feedback.yes')}
					</button>
					<button
						type="button"
						className={classes
							.elem('feedback-btn')
							.mod({ active: rating === 1 })
							.b()}
						aria-pressed={rating === 1}
						onClick={() => onRate(1)}
					>
						<Icon name="thumbs-down" /> {t('ai.dealForecast.feedback.no')}
					</button>
				</div>
			</div>
			{rating !== null && (
				<div className={classes.elem('feedback-note').b()}>
					<Textarea
						autoFocus
						value={comment}
						onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
						placeholder={t('ai.dealForecast.feedback.placeholder')}
					/>
					<Flex justifyContent="flex-end" gap="u2">
						<Button
							size="sm"
							type="link"
							onClick={() => {
								setRating(null);
								setComment('');
							}}
						>
							{t('default.cancel')}
						</Button>
						<Button size="sm" onClick={submit}>
							{t('ai.dealForecast.feedback.send')}
						</Button>
					</Flex>
				</div>
			)}
		</div>
	);
};

// Strip leading bullet / dash / numbering markers the LLM sometimes prefixes
// onto array items. We render them inside a <ul> already, so the duplicate
// marker shows up as a double bullet.
const stripLeadingBullet = (s: string) => s.replace(/^\s*(?:[•\-*·]|\d+[.)])\s+/, '');

const BulletList = ({ items }: { items: string[] }) => (
	<ul className={classes.elem('bullets').b()}>
		{items.map((item, i) => (
			<li key={i}>{stripLeadingBullet(item)}</li>
		))}
	</ul>
);

const Section = ({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) => (
	<div className={classes.elem('section').b()}>
		<div className={classes.elem('section-head').b()}>
			<Text bold size="sm" className={classes.elem('section-title').b()}>
				{title}
			</Text>
			{count !== undefined && <span className={classes.elem('count-chip').b()}>{count}</span>}
		</div>
		{children}
	</div>
);

type BannerState = 'empty' | 'loading' | 'loaded';

const BannerHeader = ({
	forecast,
	refreshing,
	onToggle,
	onReAnalyze,
	mode
}: {
	forecast: ForecastPayload;
	refreshing: boolean;
	onToggle: () => void;
	onReAnalyze: () => void;
	mode: 'collapsed' | 'full';
}) => {
	const { t } = useTranslation();
	const rating = deriveRating(forecast);
	const headline = deriveHeadline(forecast);
	const analyzedLabel = forecast.analyzedAt ? String(dateCalendar(forecast.analyzedAt, true)) : null;

	return (
		<div
			className={classes.elem('header').b()}
			onClick={onToggle}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onToggle();
				}
			}}
		>
			<div className={classes.elem('header-row').b()}>
				<span className={classes.elem('tag').b()}>{t('ai.dealForecast.tab')}</span>
				<Text bold className={classes.elem('verdict').b()}>
					{t(`ai.dealForecast.rating.${rating}`)}
				</Text>
				{headline && (
					<Text size="sm" color="grey-11" className={classes.elem('headline').b()}>
						{`— ${headline}`}
					</Text>
				)}
				<div className={classes.elem('header-actions').b()}>
					{analyzedLabel && (
						<Text size="sm" color="grey-10">
							{analyzedLabel}
						</Text>
					)}
					<ReAnalyzeButton onConfirm={onReAnalyze} refreshing={refreshing} />
					<Icon name={mode === 'full' ? 'chevron-up' : 'chevron-down'} />
				</div>
			</div>
			<RatingScale rating={rating} />
			<div className={classes.elem('counts').b()}>
				<span>
					<strong>{forecast.riskSignals.length}</strong> {t('ai.dealForecast.banner.countRisks')}
				</span>
				<span className={classes.elem('dot-sep').b()}>{'·'}</span>
				<span>
					<strong>{forecast.recommendedActions.length}</strong> {t('ai.dealForecast.banner.countActions')}
				</span>
				<span className={classes.elem('dot-sep').b()}>{'·'}</span>
				<span>
					<strong>{forecast.positiveSignals.length}</strong>{' '}
					{t('ai.dealForecast.banner.countPositiveSignals')}
				</span>
				<span className={classes.elem('toggle-link').b()}>
					{mode === 'full'
						? t('ai.dealForecast.banner.hideDetails')
						: t('ai.dealForecast.banner.showDetails')}
				</span>
			</div>
		</div>
	);
};

const BannerBody = ({ forecast }: { forecast: ForecastPayload }) => {
	const { t } = useTranslation();
	const summary = Array.isArray(forecast.summary) ? forecast.summary : [forecast.summary];

	return (
		<div className={classes.elem('body').b()}>
			{summary.length > 0 && summary.some(Boolean) && (
				<Section title={t('ai.dealForecast.banner.tldr')}>
					<BulletList items={summary.filter(Boolean)} />
				</Section>
			)}

			{forecast.riskSignals.length > 0 && (
				<Section title={t('ai.dealForecast.risks')} count={forecast.riskSignals.length}>
					<BulletList items={forecast.riskSignals.map(s => s.signal)} />
				</Section>
			)}

			{forecast.recommendedActions.length > 0 && (
				<Section title={t('ai.dealForecast.actions')} count={forecast.recommendedActions.length}>
					<ul className={classes.elem('bullets').b()}>
						{forecast.recommendedActions.map((a, i) => (
							<li key={i}>
								<span className={classes.elem('action-title').b()}>{a.action}</span>
								{a.reasoning && (
									<span className={classes.elem('action-reason').b()}>{a.reasoning}</span>
								)}
							</li>
						))}
					</ul>
				</Section>
			)}

			{forecast.positiveSignals.length > 0 && (
				<Section title={t('ai.dealForecast.positiveSignals')} count={forecast.positiveSignals.length}>
					<BulletList items={forecast.positiveSignals.map(s => s.signal)} />
				</Section>
			)}

			{!forecast.stakeholderAssessment.adequate &&
				(forecast.stakeholderAssessment.missingRoles.length > 0 ||
					forecast.stakeholderAssessment.suggestedActions.length > 0) && (
					<Section title={t('ai.dealForecast.stakeholderGaps')}>
						{forecast.stakeholderAssessment.missingRoles.length > 0 && (
							<Text size="sm" className={classes.elem('section-body').b()}>
								{t('ai.dealForecast.missing')} {forecast.stakeholderAssessment.missingRoles.join(', ')}
							</Text>
						)}
						{forecast.stakeholderAssessment.suggestedActions.length > 0 && (
							<BulletList items={forecast.stakeholderAssessment.suggestedActions} />
						)}
					</Section>
				)}

			{forecast.icpMatch && (
				<Section title={t('ai.dealForecast.icpMatch')}>
					<Text bold size="sm">
						{translateEnum(t, 'icpMatchValue', forecast.icpMatch.match)}
					</Text>
					<Text size="sm" color="grey-10" className={classes.elem('section-body').b()}>
						{forecast.icpMatch.reasoning}
					</Text>
				</Section>
			)}
		</div>
	);
};

const EmptyState = ({ onAnalyze }: { onAnalyze: () => void }) => {
	const { t } = useTranslation();
	return (
		<div className={classes.elem('empty').b()}>
			<div className={classes.elem('empty-body').b()}>
				<div className={classes.elem('empty-title-row').b()}>
					<span className={classes.elem('tag').b()}>{t('ai.dealForecast.tab')}</span>
					<Text size="sm">{t('ai.dealForecast.banner.emptyTitle')}</Text>
				</div>
				<Text size="sm" color="grey-10">
					{t('ai.dealForecast.banner.emptyMeta')}
				</Text>
			</div>
			<Button size="sm" onClick={onAnalyze}>
				<Icon name="magic" /> {t('ai.dealForecast.runAnalysis')}
			</Button>
		</div>
	);
};

const LoadingState = () => {
	const { t } = useTranslation();
	return (
		<div className={classes.elem('loading').b()}>
			<Loader size="sm" />
			<Text size="sm" color="grey-10">
				{t('ai.dealForecast.analyzing')}
			</Text>
		</div>
	);
};

const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => {
	const { t } = useTranslation();
	return (
		<div className={classes.elem('error').b()}>
			<Text size="sm" color="grey-10">
				{message}
			</Text>
			<Button size="sm" type="link" onClick={onRetry}>
				{t('ai.dealForecast.retry')}
			</Button>
		</div>
	);
};

const OrderForecast = ({ orderId }: { orderId: number }) => {
	const { t } = useTranslation();
	const [data, setData] = useState<ForecastData | null>(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [mode, setMode] = useState<'collapsed' | 'full'>('collapsed');

	const cancelled = useRef(false);
	const abortRef = useRef<AbortController | null>(null);

	const fetchStored = async (signal: AbortSignal): Promise<ForecastData | null> => {
		const response = await fetch(`/api/v2/ai/deal-forecast/${orderId}`, { signal });
		if (!response.ok) {
			throw new Error(`Failed to fetch forecast: ${response.status}`);
		}
		const text = await response.text();
		if (!text) return null;
		try {
			const result = JSON.parse(text);
			return (result.data ?? result) as ForecastData | null;
		} catch {
			throw new Error('Invalid response from server');
		}
	};

	const wait = (ms: number) =>
		new Promise<void>((resolve, reject) => {
			const signal = abortRef.current?.signal;
			let timer: ReturnType<typeof setTimeout> | null = null;
			const onAbort = () => {
				if (timer) clearTimeout(timer);
				reject(new DOMException('aborted', 'AbortError'));
			};
			timer = setTimeout(() => {
				signal?.removeEventListener('abort', onAbort);
				resolve();
			}, ms);
			signal?.addEventListener('abort', onAbort, { once: true });
		});

	const pollUntilSettled = async (signal: AbortSignal) => {
		const deadline = Date.now() + POLL_TIMEOUT_MS;
		while (Date.now() < deadline) {
			await wait(POLL_INTERVAL_MS);
			if (cancelled.current) return;
			const latest = await fetchStored(signal);
			if (cancelled.current) return;
			if (!latest) continue;
			setData(latest);
			if (latest.jobState === 'completed' || latest.jobState === 'failed') {
				if (latest.jobState === 'failed') setError(t('ai.dealForecast.failed'));
				return;
			}
		}
		setError(t('ai.dealForecast.timedOut'));
	};

	const loadInitial = async () => {
		const controller = new AbortController();
		abortRef.current = controller;
		try {
			setLoading(true);
			setError(null);
			const latest = await fetchStored(controller.signal);
			if (cancelled.current) return;
			if (latest) {
				setData(latest);
				if (latest.jobState === 'queued' || latest.jobState === 'running') {
					setRefreshing(true);
					await pollUntilSettled(controller.signal);
					if (!cancelled.current) setRefreshing(false);
				} else if (latest.jobState === 'failed') {
					setError(t('ai.dealForecast.failed'));
				}
			}
		} catch (err) {
			if ((err as { name?: string })?.name === 'AbortError') return;
			logError(err, 'OrderForecast');
			if (!cancelled.current) setError(t('ai.dealForecast.failed'));
		} finally {
			if (!cancelled.current) setLoading(false);
		}
	};

	const runAnalysis = async () => {
		const controller = abortRef.current ?? new AbortController();
		abortRef.current = controller;
		try {
			setRefreshing(true);
			setError(null);
			const response = await fetch(`/api/v2/ai/deal-forecast/${orderId}/analyze`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ depth: 'deep' }),
				signal: controller.signal
			});
			if (cancelled.current) return;
			if (!response.ok) {
				throw new Error(`Failed to run analysis: ${response.status}`);
			}
			const result = await response.json();
			const enqueued = (result.data ?? result) as EnqueueResponse;
			setData(prev =>
				prev
					? { ...prev, forecastId: enqueued.forecastId, jobState: enqueued.jobState, forecast: null }
					: { forecastId: enqueued.forecastId, jobState: enqueued.jobState, forecast: null }
			);
			setMode('full');
			await pollUntilSettled(controller.signal);
		} catch (err) {
			if ((err as { name?: string })?.name === 'AbortError') return;
			logError(err, 'OrderForecast');
			if (!cancelled.current) setError(t('ai.dealForecast.failed'));
		} finally {
			if (!cancelled.current) setRefreshing(false);
		}
	};

	useEffect(() => {
		cancelled.current = false;
		if (orderId) {
			loadInitial();
		}
		return () => {
			cancelled.current = true;
			abortRef.current?.abort();
			abortRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId]);

	const state: BannerState = useMemo(() => {
		if (loading && !data) return 'loading';
		if (data?.jobState === 'queued' || data?.jobState === 'running') return 'loading';
		if (!data?.forecast) return 'empty';
		return 'loaded';
	}, [loading, data]);

	if (state === 'loading') {
		return (
			<div className={classes.mod({ loading: true }).b()}>
				<LoadingState />
			</div>
		);
	}

	if (error && !data?.forecast) {
		const retry = data?.jobState === 'failed' ? () => runAnalysis() : () => loadInitial();
		return (
			<div className={classes.mod({ error: true }).b()}>
				<ErrorState message={error} onRetry={retry} />
			</div>
		);
	}

	if (state === 'empty' || !data?.forecast) {
		return (
			<div className={classes.mod({ empty: true }).b()}>
				<EmptyState onAnalyze={runAnalysis} />
			</div>
		);
	}

	const forecast = data.forecast;

	return (
		<div className={classes.mod({ expanded: mode === 'full', collapsed: mode === 'collapsed' }).b()}>
			<BannerHeader
				forecast={forecast}
				refreshing={refreshing}
				onToggle={() => setMode(mode === 'collapsed' ? 'full' : 'collapsed')}
				onReAnalyze={runAnalysis}
				mode={mode}
			/>
			{mode === 'full' && (
				<>
					<BannerBody forecast={forecast} />
					<FeedbackBar forecastId={data.forecastId} orderId={orderId} />
				</>
			)}
		</div>
	);
};

export default OrderForecast;
